import React, {Component} from "react";
import PropTypes from "prop-types";
import {
    REFEREE_STATE,
    SERVER_ADDRESS_HTTP
} from "../GlobalDefinitions";

const PANEL_STYLE = {
    minHeight: "100vh",
    background: "var(--backgroundDark)",
    color: "white",
    padding: "24px 16px 48px",
    boxSizing: "border-box",
};

const CARD_STYLE = {
    background: "rgba(0, 0, 0, 0.35)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "16px",
    maxWidth: "920px",
    margin: "0 auto 16px",
};

function getRoleColor(role) {
    if (role === "LIBERAL") {
        return "var(--textColorLiberal)";
    }
    if (role === "HITLER") {
        return "#f5d76e";
    }
    return "#ff8c69";
}

class RefereePanel extends Component {
    refreshInterval = undefined;

    constructor(props) {
        super(props);

        this.state = {
            username: "",
            password: "",
            error: "",
            loading: false,
            authenticated: false,
            lobbies: [],
            updatedAt: "",
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.fetchRefereeState = this.fetchRefereeState.bind(this);
    }

    componentWillUnmount() {
        clearInterval(this.refreshInterval);
    }

    async fetchRefereeState(username, password) {
        const params = new URLSearchParams({
            username,
            password,
        });
        const response = await fetch(`${SERVER_ADDRESS_HTTP}${REFEREE_STATE}?${params.toString()}`);
        if (!response.ok) {
            let message = "Unable to load referee panel.";
            if (response.status === 401) {
                message = "Invalid referee credentials.";
            }
            throw new Error(message);
        }
        return response.json();
    }

    async handleSubmit(event) {
        event.preventDefault();

        const username = this.state.username.trim();
        const password = this.state.password;

        this.setState({
            loading: true,
            error: "",
        });

        try {
            const data = await this.fetchRefereeState(username, password);
            clearInterval(this.refreshInterval);
            this.refreshInterval = setInterval(async () => {
                try {
                    const refreshed = await this.fetchRefereeState(username, password);
                    this.setState({
                        lobbies: refreshed.lobbies || [],
                        updatedAt: new Date().toLocaleTimeString(),
                    });
                } catch (error) {
                    this.setState({error: error.message});
                }
            }, 3000);

            this.setState({
                authenticated: true,
                loading: false,
                lobbies: data.lobbies || [],
                updatedAt: new Date().toLocaleTimeString(),
            });
        } catch (error) {
            this.setState({
                loading: false,
                authenticated: false,
                error: error.message,
            });
        }
    }

    renderLobby(lobby) {
        return (
            <div key={lobby.code} style={CARD_STYLE}>
                <div style={{display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap"}}>
                    <div>
                        <h2 style={{margin: "0 0 4px"}}>Lobby {lobby.code}</h2>
                        <p style={{margin: 0, opacity: 0.8}}>
                            {lobby["in-game"] ? "Game running" : "Waiting in lobby"} | {lobby["user-count"]} connected
                        </p>
                    </div>
                    <div style={{textAlign: "right"}}>
                        <button onClick={this.handleSubmit}>Refresh</button>
                    </div>
                </div>

                {lobby.players && lobby.players.length > 0 ? (
                    <div style={{marginTop: "16px", display: "grid", gap: "10px"}}>
                        {lobby.players.map(player => (
                            <div key={`${lobby.code}-${player.name}`}
                                 style={{
                                     display: "flex",
                                     justifyContent: "space-between",
                                     alignItems: "center",
                                     gap: "12px",
                                     flexWrap: "wrap",
                                     padding: "12px",
                                     borderRadius: "10px",
                                     background: "rgba(255, 255, 255, 0.04)",
                                 }}>
                                <div>
                                    <div style={{fontSize: "1.05rem", fontWeight: 700}}>{player.name}</div>
                                    <div style={{opacity: 0.75, fontSize: "0.9rem"}}>
                                        {player.alive ? "Alive" : "Executed"}{player.investigated ? " | Investigated" : ""}
                                    </div>
                                </div>
                                <div style={{
                                    color: getRoleColor(player.role),
                                    fontWeight: 800,
                                    letterSpacing: "0.06em",
                                }}>
                                    {player.role}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{marginTop: "16px", opacity: 0.8}}>
                        No active game in this lobby yet.
                    </div>
                )}
            </div>
        );
    }

    render() {
        return (
            <div style={PANEL_STYLE}>
                <div style={{maxWidth: "920px", margin: "0 auto"}}>
                    <h1 style={{marginTop: 0}}>Referee Panel</h1>
                    <p style={{opacity: 0.82}}>
                        Hidden panel for role visibility across active lobbies.
                    </p>

                    <form onSubmit={this.handleSubmit} style={{...CARD_STYLE, marginBottom: "20px"}}>
                        <div style={{display: "grid", gap: "12px"}}>
                            <label>
                                <div style={{marginBottom: "6px"}}>Username</div>
                                <input
                                    type="text"
                                    value={this.state.username}
                                    onChange={(event) => this.setState({username: event.target.value})}
                                    autoComplete="off"
                                />
                            </label>
                            <label>
                                <div style={{marginBottom: "6px"}}>Password</div>
                                <input
                                    type="password"
                                    value={this.state.password}
                                    onChange={(event) => this.setState({password: event.target.value})}
                                    autoComplete="off"
                                />
                            </label>
                            <div style={{display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap"}}>
                                <button type="submit" disabled={this.state.loading}>
                                    {this.state.loading ? "Loading..." : "Enter /0x"}
                                </button>
                                {this.state.updatedAt ? (
                                    <span style={{opacity: 0.75}}>Last updated: {this.state.updatedAt}</span>
                                ) : null}
                            </div>
                            {this.state.error ? (
                                <div style={{color: "#ff8c69"}}>{this.state.error}</div>
                            ) : null}
                        </div>
                    </form>

                    {this.state.authenticated ? (
                        this.state.lobbies.length > 0 ? (
                            this.state.lobbies.map(lobby => this.renderLobby(lobby))
                        ) : (
                            <div style={CARD_STYLE}>No active lobbies found.</div>
                        )
                    ) : (
                        <div style={CARD_STYLE}>
                            Authenticate to reveal lobby roles.
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

RefereePanel.propTypes = {
    path: PropTypes.string,
};

export default RefereePanel;
